/** Rappen-level slack, so Swiss rounding never raises a false alarm. */
const TOLERANCE = 0.05;

export interface TotalMismatch {
  /** The grand total the model read off the receipt. */
  scannedTotal: number;
  /** What the extracted line items actually add up to. */
  itemSum: number;
  /** itemSum − scannedTotal: negative means items are missing. */
  diff: number;
}

/** Sum of the amounts that count, mirroring the total shown in Edit.tsx. */
export function sumItems(items: Array<{ amount: number }>): number {
  return items.reduce((s, i) => s + (Number.isFinite(i.amount) ? i.amount : 0), 0);
}

/**
 * Compare the receipt's printed total against the line items we recovered.
 *
 * Edit.tsx displays — and submits — the sum of the line items, so when the
 * model reads a total it could not itemise (a page missing from a multi-page
 * invoice, a column it could not parse), the wrong number goes through
 * silently. Returns null when the two agree, or there is no total to compare.
 */
export function totalMismatch(
  scannedTotal: number,
  items: Array<{ amount: number }>,
): TotalMismatch | null {
  if (!Number.isFinite(scannedTotal) || scannedTotal === 0) return null;
  const itemSum = sumItems(items);
  const diff = itemSum - scannedTotal;
  if (Math.abs(diff) <= TOLERANCE) return null;
  return { scannedTotal, itemSum, diff: Number(diff.toFixed(2)) };
}
